import Icon from '@/components/atoms/Icon'
import { Menu, Transition } from '@headlessui/react'
import { twMerge } from 'tailwind-merge'
import classNames from 'classnames'
import { Fragment } from 'react'
import { Partner } from '@/models/mentorships.model'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { modalCreators } from '@/store/ducks/modal'
import mentorshipsService from '@/services/mentorshipsService'

interface CardMentorshipsProps {
	mentorship: {
		id: number
		date: string
		duration: string
		calendarEventLink: string
		notEditable: boolean
		slug: string
		partner: Partner
	}
	mentoringIsHappening: boolean
	mentoringInThePast: boolean
	className?: string
	isLoading?: boolean
}

interface OptionsProps {
	options?: Array<{
		onClick: CallableFunction
		label: string
		className?: string
		disabled?: boolean
		roles?: boolean
	}>
}

export function CardMentoring({
	mentorship,
	mentoringIsHappening,
	mentoringInThePast,
	className,
	isLoading,
	options,
}: CardMentorshipsProps & OptionsProps) {
	const { execute: attendMentorship } = mentorshipsService.attendMentorship()

	const partner = mentorship?.partner || {
		name: '',
		lastName: '',
		companyRole: '',
		companyName: '',
		profilePicture: undefined,
	}

	const fullname =
		partner?.name || partner?.lastName
			? `${partner?.name} ${partner?.lastName || ''}`
			: `Mentor(a)`

	const job =
		partner?.companyRole && partner?.companyName
			? `${partner?.companyRole} at ${partner?.companyName}`
			: 'Mentor(a) da Fluency'

	return (
		<div
			className={twMerge(
				'flex bg-white border border-white/10 dark:bg-[#1E1528] w-full flex-col shadow-course rounded-lg py-6 px-5 md:px-6 lg:px-7 transition-all duration-300',
				classNames(
					className,
					{ 'animate-pulse': isLoading },
					{
						'opacity-30 border dark:opacity-25 dark:border-white/5 shadow-2xl dark:shadow-xl pointer-events-none':
							mentoringInThePast,
					},
					{
						'border border-[#fb4982] dark:border-brand-primary':
							mentoringIsHappening,
					}
				)
			)}
		>
			<div className='flex flex-col xl:flex-row gap-2 xl:gap-0 justify-between xl:items-center'>
				<div className='flex flex-col xl:flex-row gap-2 max-w-[400px]'>
					{mentoringIsHappening && (
						<div className='flex xl:hidden gap-2 items-center'>
							<div className='w-2 h-2 rounded-full bg-[#fb4982] dark:bg-brand-primary' />
							<p className='text-[#fb4982] dark:text-brand-primary text-base font-medium'>
								Mentoria acontecendo agora
							</p>
						</div>
					)}
					<div className='flex items-center gap-6'>
						<div
							className={twMerge(
								'flex md:flex items-start',
								classNames({ 'hidden md:flex': mentoringIsHappening })
							)}
						>
							<div className='flex relative'>
								{(() => {
									if (partner?.profilePicture) {
										return (
											<img
												src={`${process.env.REACT_APP_IMAGE_PROFILE_URL}${partner?.profilePicture}`}
												alt='Avatar Mentor'
												className='min-w-[52px] min-h-[52px] max-w-[52px] max-h-[52px] object-fill rounded-full'
												loading='lazy'
											/>
										)
									}

									return (
										<Icon
											name='default-avatar'
											width='52'
											height='52'
											className='min-w-[52px] min-h-[52px] max-w-[52px] max-h-[52px] object-fill border-4 rounded-full'
										/>
									)
								})()}

								{(() => {
									if (partner?.companyPicture) {
										return (
											<img
												src={`${process.env.REACT_APP_IMAGE_PROFILE_URL}${partner?.companyPicture}`}
												alt='Avatar Company Mentor'
												className='min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px] absolute -bottom-1 -right-2 object-fill rounded-full'
											/>
										)
									}

									return (
										<Icon
											name='default-avatar'
											width='52'
											height='52'
											className='min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px] absolute -bottom-1 -right-2 object-fill rounded-full'
										/>
									)
								})()}
							</div>
						</div>

						<div className='flex flex-col gap-1'>
							<span className='font-medium text-xl text-white leading-[104%]'>
								{fullname}
							</span>
							<p className='hidden md:flex font-medium text-base text-white/70 max-w-[720px] leading-[150%] -webkit-line-clamp-3'>
								{job}
							</p>
						</div>
					</div>
				</div>
				{mentoringIsHappening && (
					<div className='hidden xl:flex gap-3 items-center h-auto rounded-full px-4 py-2 bg-white'>
						<div className='w-2 h-2 rounded-full bg-[#fb4982] dark:bg-brand-primary' />
						<p className='text-[#fb4982] dark:text-brand-primary text-sm font-medium'>
							Acontecendo agora
						</p>
					</div>
				)}
				<div className='flex flex-col xl:flex-row items-start xl:items-center gap-5 xl:gap-7'>
					{mentorship.date &&
						(() => {
							if (mentoringIsHappening) {
								return (
									<>
										<div className='flex xl:hidden flex-col items-end'>
											<p className='text-sm font-medium text-white/70'>
												Dia{' '}
												{dayjs(mentorship.date)
													.locale('pt-br')
													.format('DD/MM [às] HH[h]mm')}{' '}
												- {mentorship.duration}min
											</p>
										</div>
										<Link
											to={mentorship?.calendarEventLink || '#'}
											onClick={() => {
												if (!mentorship?.id) {
													modalCreators.openModal({
														name: 'statusModal',
														type: 'error',
														modalInfo: {
															title: `Erro em mentoria`,
															buttonLabel: 'Fechar',
															message: `Erro ao tentar acessar a mentoria.`,
														},
													})
												}

												attendMentorship({
													mentorshipId: mentorship?.id?.toString(),
												})
											}}
											target='_blank'
											className='flex border-none w-full max-w-[170px] h-[40px] bg-[#fb4982] dark:bg-brand-primary border overflow-hidden outline-none rounded-md md:rounded-full p-3 text-white stroke-white text-base justify-center items-center relative transition-colors ease-linear duration-100 px-8 font-medium disabled:bg-details-outline disabled:pointer-events-none disabled:opacity-50'
										>
											Acessar sala
										</Link>
									</>
								)
							}

							return (
								<>
									<div className='flex xl:hidden flex-col items-end'>
										<p className='text-sm font-medium text-white/70'>
											Dia{' '}
											{dayjs(mentorship.date)
												.locale('pt-br')
												.format('DD/MM [às] HH[h]mm')}{' '}
											- {mentorship.duration}min
										</p>
									</div>
									<div className='hidden xl:flex flex-col items-end'>
										<p className='text-lg font-medium text-white'>
											Dia{' '}
											{dayjs(mentorship.date)
												.locale('pt-br')
												.format('DD [de] MMMM')}
										</p>

										<p className='text-base font-medium text-white/70 leading-[150%]'>
											{dayjs(mentorship.date).locale('pt-br').format('HH[h]mm')}{' '}
											- {mentorship.duration}min
										</p>
									</div>
								</>
							)
						})()}
					<div
						className={twMerge(
							'flex',
							classNames({
								'hidden xl:flex': mentoringInThePast || mentoringIsHappening,
							})
						)}
					>
						{options && (
							<div>
								<Menu as='div' className='relative inline-block text-left'>
									{({ open }) => (
										<>
											<div>
												<Menu.Button className='inline-flex items-center justify-center gap-2'>
													<p
														className={twMerge(
															'text-white/70 font-medium text-base leading-none transition-all duration-500',
															classNames({
																'text-white': open,
															})
														)}
													>
														Opções
													</p>
													<Icon
														name='chevron-down'
														fill='none'
														stroke='rgb(132 147 172)'
														width='13'
														height='13'
														className={`mt-[1px] transition-all duration-500 ${classNames({ 'rotate-180 stroke-white': open })}`}
													/>
												</Menu.Button>
											</div>
											<Transition
												as={Fragment}
												enter='transition ease-out duration-300'
												enterFrom='transform opacity-0 scale-95'
												enterTo='transform opacity-100 scale-100'
												leave='transition ease-in duration-75'
												leaveFrom='transform opacity-100 scale-100'
												leaveTo='transform opacity-0 scale-95'
											>
												<Menu.Items className='absolute left-0 overflow-clip z-10 mt-2 w-[150px] origin-bottom-right ring-2 ring-slate-50 rounded-md bg-white dark:bg-[#23192F] dark:ring-white/5 shadow-lg focus:outline-none'>
													<div className='py-1'>
														{options?.map(
															(option) =>
																option?.roles && (
																	<Menu.Item
																		key={option.label}
																		disabled={option.disabled}
																	>
																		{({ active, close }) => (
																			<button
																				className={twMerge(
																					classNames(
																						active
																							? 'bg-gray-100 text-[#000000]'
																							: 'text-white/80',
																						'w-full items-start flex px-4 py-2 text-sm font-medium'
																					),
																					option.className
																				)}
																				onClick={(e) => {
																					e.preventDefault()
																					close()
																					option.onClick()
																				}}
																			>
																				{option.label}
																			</button>
																		)}
																	</Menu.Item>
																)
														)}
													</div>
												</Menu.Items>
											</Transition>
										</>
									)}
								</Menu>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
